import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  ConversationLadder,
  FacilityChecklist,
  FacilitySearchResponse,
  GoLiveQueue,
  MeAssignment,
  MetricsMonitoring,
  MetricsOverview,
  OnboardingMe,
  OperatorFleet,
  OverviewCensus,
  OverviewQueues,
  OverviewSummary,
  OverviewWorklistResponse,
  PolicyPosture,
  TestPushResponse,
  TestStatus,
  EscalationTestResponse,
  GoLiveNudgeResponse,
} from './overview-types';
import type { BuildingOverview, OverviewTriage, RevenueIntegrity, ShiftOverview } from './building-types';

const A = AITHORA_PREFIX;

// Routes marked honest promise the `real` flag and are refused without it. The rest
// answer counts or Measures (flag in provenance.method) with no top-level `real`, so
// the page badges each figure from its own flag, or draws none.

/** GET /overview/summary: orgs, facilities, residents and beds under management. */
export const overviewSummary = route<OverviewSummary>()('GET', `${A}/overview/summary`);

/** GET /overview/conversations: today / week / 14 days / all time. */
export const overviewConversations = route<ConversationLadder>()('GET', `${A}/overview/conversations`);

/** GET /overview/worklist: unread alerts and open care requests, severity first. */
export const overviewWorklist = route<OverviewWorklistResponse>()('GET', `${A}/overview/worklist`);

/** GET /overview/census?days=: one building's census band. */
export const overviewCensus = route<OverviewCensus, { days?: number }>()('GET', `${A}/overview/census`);

/** GET /overview/queues: families waiting, awaiting a nurse, twelve-month occupancy. */
export const overviewQueues = route<OverviewQueues>()('GET', `${A}/overview/queues`);

/** GET /operator/fleet: the caller's buildings with a CMS grade each. */
export const operatorFleet = route<OperatorFleet, { operatorOrgId?: string }>()('GET', `${A}/operator/fleet`);

/** GET /api/metrics/overview?windowDays=14: conversations by day, verdict mix, guard. */
export const metricsOverview = route<MetricsOverview, { windowDays?: number }>()('GET', '/api/metrics/overview');

/** GET /api/metrics/monitoring: latency, verdicts, hourly trend, held queue, guard. */
export const metricsMonitoring = route<MetricsMonitoring, { windowMinutes?: number; windowHours?: number }>()('GET', '/api/metrics/monitoring');

/** GET /policy/posture?scope=network|<facility id>: SAFE / DEGRADED / HALTED and why. */
export const policyPosture = route<PolicyPosture, { scope?: string }>()('GET', `${A}/policy/posture`, { honest: true });

/** GET /onboarding/facility/:id: the Facility Admin setup checklist. */
export const facilityChecklist = route<FacilityChecklist>()('GET', `${A}/onboarding/facility/:id`, { honest: true });

/** PUT /onboarding/facility/:id/prefs: per-user dismiss / collapse of the checklist. */
export const facilityChecklistPrefs = route<{ real: true; dismissed: boolean; collapsed: boolean }, void, { dismissed?: boolean; collapsed?: boolean }>()(
  'PUT', `${A}/onboarding/facility/:id/prefs`, { honest: true });

/** GET /me/assignment: the Care Staff shift (unit, window, residents). */
export const meAssignment = route<MeAssignment>()('GET', `${A}/me/assignment`, { honest: true });

/** GET /onboarding/me: the caller's own setup (Care Staff: n of 3). */
export const onboardingMe = route<OnboardingMe>()('GET', `${A}/onboarding/me`);

/** GET /onboarding/go-live: the Operator's go-live queue. */
export const goLiveQueue = route<GoLiveQueue>()('GET', `${A}/onboarding/go-live`, { honest: true });

/** POST /notify/test-push: page yourself to prove alerts reach your phone. */
export const testPush = route<TestPushResponse, void, { user_id?: string; confirm?: boolean }>()('POST', `${A}/notify/test-push`, { honest: true });

/** GET /facilities/search?q=&limit=: server-side search, scoped by the server. */
export const facilitySearch = route<FacilitySearchResponse, { q: string; limit?: number }>()('GET', `${A}/facilities/search`, { honest: true });

/** GET /notify/test/:id: a test page's delivery and acknowledgement (ackSeconds once tapped). */
export const testStatus = route<TestStatus>()('GET', `${A}/notify/test/:id`, { honest: true });

/** POST /escalation/test: ring the nurse on call as a TEST (Facility Admin: own building). */
export const escalationTest = route<EscalationTestResponse, void, { organizationId?: string; confirm?: boolean }>()('POST', `${A}/escalation/test`, { honest: true });

/** POST /notify/test/:id/ack: the person a test page went to says it arrived ("I got it"). Only them. */
export const testAck = route<{ real: true; testId: string; kind?: string; acknowledgedAt: string | null; ackSeconds: number | null; already?: boolean }, void, Record<string, never>>()(
  'POST', `${A}/notify/test/:id/ack`, { honest: true });

/** GET /notify/test/pending: test pages sent to the caller in the last 30 minutes, not yet acknowledged. */
export const testPending = route<{ real: true; tests: Array<{ testId: string; kind: string; sentAt: string; fromName: string | null }> }>()(
  'GET', `${A}/notify/test/pending`, { honest: true });

/** POST /onboarding/facility/:id/confirm-profile: setup step 1, the building is right. */
export const facilityConfirmProfile = route<{ real: true; profileConfirmedAt: string }, void, Record<string, never>>()(
  'POST', `${A}/onboarding/facility/:id/confirm-profile`, { honest: true });

/** The record kept when a BAA is recorded as signed by both sides (src/aithora/baa-gate.ts). */
export interface BaaRecord {
  facilitySigner: string;
  aithoraSigner: string;
  effectiveDate: string;
  documentRef: string;
  recordedBy: string | null;
  recordedAt: string;
}

/** POST /onboarding/facility/:id/baa {status}: Aithora records where a building's BAA stands. Aithora staff only.
 *  'countersigned' (signed by both sides) unlocks loading residents and needs bothSigned, both signers, the
 *  effective date and where the signed copy is kept. */
export const facilityBaaStatus = route<{ real: true; baaStatus: string; baaRecord?: BaaRecord | null }, void, {
  status: 'none' | 'sent' | 'signed' | 'countersigned';
  bothSigned?: boolean; facilitySigner?: string; aithoraSigner?: string; effectiveDate?: string; documentRef?: string;
}>()(
  'POST', `${A}/onboarding/facility/:id/baa`, { honest: true });

/** PUT /onboarding/me {practice?, atlas?}: the Care Staff steps a person marks themselves. */
export const onboardingMeSave = route<OnboardingMe, void, { practice?: boolean; atlas?: boolean }>()('PUT', `${A}/onboarding/me`);

/** POST /onboarding/:facilityId/nudge: SMS + email to the Facility Admin contact, logged. */
export const goLiveNudge = route<GoLiveNudgeResponse, void, Record<string, never>>()('POST', `${A}/onboarding/:facilityId/nudge`, { honest: true });

/** POST /onboarding/:facilityId/take: "Take it", the caller becomes the Aithora owner. */
export const goLiveTake = route<{ real: true; facilityId: string; ownerUserId: string }, void, Record<string, never>>()('POST', `${A}/onboarding/:facilityId/take`, { honest: true });

/** GET /overview/building (src/aithora/v3/overview/building.ts): the Facility Admin's own
 *  building, every block the design draws. Facility Admin only; another building is 403. */
export const overviewBuilding = route<BuildingOverview>()('GET', `${A}/overview/building`, { honest: true });

/** What POST /overview/staffing/agency-request answers: the new request, or the open one (`already`). */
export type AgencyRequestResult =
  | { ok: true; already: true; id: string }
  | { ok: true; already?: undefined; id: string; shift: 'Days' | 'Evenings' | 'Nights'; shiftDay: string; unit: string | null; aidesNeeded: number; notified: number; requestedAt: string };
/**
 * POST /overview/staffing/agency-request (v3/overview/staffing.ts): the Facility Admin's
 * Request agency. Records it and tells the building's administrators; one open request per
 * shift and wing, so a second tap returns the first. Aithora books nothing.
 */
export const staffingAgencyRequest = route<AgencyRequestResult, void, { unit?: string; aidesNeeded?: number; note?: string }>()('POST', `${A}/overview/staffing/agency-request`);

// ── Served (src/aithora/v3/overview/*, v3/pulse.ts) ──

/** GET /overview/shift: the Care Staff shift worklist with due times. */
export const overviewShift = route<ShiftOverview>()('GET', `${A}/overview/shift`, { honest: true });

/** GET /overview/triage: residents the eight detectors flagged (portfolio, or the caller's building). */
/** ?facilities=id,id: the facility switcher's pick (Aithora readers only). */
export const overviewTriage = route<OverviewTriage, { facilities?: string }>()('GET', `${A}/overview/triage`, { honest: true });

/** POST /overview/triage/:id/assign {userId}: place a flagged resident with someone (Facility Admin). */
export const triageAssign = route<{ real: true; id: string; owner: { userId: string; name: string } }, void, { userId: string }>()(
  'POST', `${A}/overview/triage/:id/assign`, { honest: true });

/** GET /overview/revenue-integrity: measured, opportunity and recoverable, never summed; platform and ledger lines. */
export const revenueIntegrity = route<RevenueIntegrity, { facilities?: string; period?: string }>()('GET', `${A}/overview/revenue-integrity`, { honest: true });

/** POST /pulse-v3/facilities/:id/chain-test-request: a Facility Admin asks Aithora to book a chain test. */
export const chainTestRequest = route<{ real: true; requestedAt: string }, void, Record<string, never>>()('POST', `${A}/pulse-v3/facilities/:id/chain-test-request`, { honest: true });

/** POST /pulse-v3/facilities/:id/problem-report: a Facility Admin reports a problem to the Aithora on-call. */
export const problemReport = route<{ real: true; reportedAt: string }, void, { note?: string }>()('POST', `${A}/pulse-v3/facilities/:id/problem-report`, { honest: true });

/** POST /overview/shift/:taskId/outcome: close a shift stop with what happened (vitals when it is a vitals stop). */
export const shiftOutcome = route<{ real: true; taskId: string; outcome: string; pagedNurse: boolean }, void, { outcome: string; vitals?: { bp?: string; hr?: string; temp?: string; spo2?: string }; flagToNurse?: boolean }>()(
  'POST', `${A}/overview/shift/:taskId/outcome`, { honest: true });

/** POST /overview/shift/fluids {residentId, ml}: log a cup. */
export const shiftFluid = route<{ real: true; residentId: string; mlToday: number }, void, { residentId: string; ml: number }>()('POST', `${A}/overview/shift/fluids`, { honest: true });

/** GET /overview/survey?facility=: the building's CMS survey timing (the date the surveyor export's "Since last survey" starts at). */
export interface OverviewSurvey {
  real: boolean;
  facilityId: string;
  available: boolean;
  /** YYYY-MM-DD, or null when the CMS catalog has no standard survey date for the building. */
  lastStandardSurvey?: string | null;
  reason?: string;
  // What the server already sends beside it (v3/overview/sources.ts surveyTiming), read by
  // Performance > Survey ready.
  /** YYYY-MM-DD: the standard survey before the last one. */
  priorStandardSurvey?: string | null;
  monthsSinceLast?: number | null;
  /** YYYY-MM-DD: 15 months after the last standard survey (42 CFR 488.308). */
  dueBy?: string | null;
  dueByBasis?: string;
  overTwoYears?: boolean | null;
  /** The last survey's results as CMS reports them. */
  lastSurvey?: { healthInspectionRating: number | null; deficiencies: number | null; severeDeficiencies: number | null; infectionDeficiencies: number | null };
  /** The state's median for the same building type (null under the small-cell floor). */
  stateMedian?: { state: string | null; peers: number; healthInspectionRating: number | null; deficiencies: number | null; severeDeficiencies: number | null; infectionDeficiencies: number | null; reason: string | null };
  basis?: 'reported';
  source?: string;
  /** YYYY-MM-DD: the CMS processing date of the catalog row. */
  vintage?: string | null;
}
export const overviewSurvey = route<OverviewSurvey, { facility?: string }>()('GET', `${A}/overview/survey`, { honest: true });

/** POST /overview/huddle/:residentId/done {done}: mark a huddle resident seen (or not). */
export const huddleDone = route<{ real: true; residentId: string; doneAt: string | null }, void, { done: boolean }>()('POST', `${A}/overview/huddle/:residentId/done`, { honest: true });

export const overviewApi = {
  overviewBuilding,
  overviewSurvey,
  staffingAgencyRequest,
  overviewShift,
  overviewTriage,
  triageAssign,
  revenueIntegrity,
  goLiveNudge,
  goLiveTake,
  testStatus,
  escalationTest,
  testAck,
  testPending,
  facilityConfirmProfile,
  facilityBaaStatus,
  onboardingMeSave,
  chainTestRequest,
  problemReport,
  shiftOutcome,
  shiftFluid,
  huddleDone,
  overviewSummary,
  overviewConversations,
  overviewWorklist,
  overviewCensus,
  overviewQueues,
  operatorFleet,
  metricsOverview,
  metricsMonitoring,
  policyPosture,
  facilityChecklist,
  facilityChecklistPrefs,
  meAssignment,
  onboardingMe,
  goLiveQueue,
  testPush,
  facilitySearch,
} as const;
